/**
 * Jurisdiction → Tax Category mapping (shared helper).
 *
 * Feb 6, 2026 — Single source of truth for "given this jurisdiction's
 * jurisdiction_type, which tax_category_code should the assignment
 * resolver be queried with?"
 *
 * Used by:
 *   • JurisdictionCollectorAssignmentsTab.jsx — to pre-default the Add
 *     Assignment modal's category dropdown for PA jurisdictions.
 *   • JurisdictionManager.jsx (Fulfillment surface) — to pass
 *     `taxCategoryCode` into CollectorGuidanceBanner and TaxCollectorPicker
 *     so the resolver sees the explicit-category rows. Without this, the
 *     resolver receives `tax_category_code=None`, which (by design) only
 *     matches NULL catch-all assignment rows and falls through to legacy
 *     fallback when only explicit-category rows exist. See
 *     services/collector_resolver.py lines 269–279 for the asymmetric
 *     category filter that makes this mapping critical on the read side.
 *
 * Mapping (matches what the backend cutover paths already pass for the
 * MLC PDF (P3.1), Tax Cert PDF (P3.2), and Mail-Away gate (P3.3)
 * consumers):
 *
 *   • county / parish                                  → "county"
 *   • township / borough / city / town / village /
 *     municipality                                     → "municipal"
 *   • school_district / school                         → "school"
 *   • (any type) + tax_period=delinquent               → "tax_claim"
 *     (PA delinquent routes through county tax claim bureaus)
 *   • unknown / unmapped type                          → ""
 *     (empty string = let the caller decide; the picker/banner will
 *     fall back to a NULL-category query, which only matches catch-all
 *     rows — the safe legacy behavior.)
 *
 * Scope note: this helper is state-agnostic by design — the *helper*
 * always returns a category if it can map the type. Callers gate on
 * `state === 'PA'` before deciding whether to pass it.
 */
export function defaultCategoryForPA(jurisdictionType, taxPeriod) {
  if (taxPeriod === 'delinquent') return 'tax_claim';
  const t = String(jurisdictionType || '').toLowerCase();
  if (t === 'county' || t === 'parish') return 'county';
  if (t === 'school_district' || t === 'school') return 'school';
  if (['township', 'borough', 'city', 'town', 'village', 'municipality'].includes(t)) {
    return 'municipal';
  }
  return '';
}

/**
 * Resolve the tax category code to pass into the assignment resolver
 * for a (jurisdiction, period) pair.
 *
 * Returns a non-empty string when the jurisdiction is PA-state AND the
 * type maps cleanly. Returns null otherwise (caller will then receive
 * legacy-only resolver behavior, which is the safe pre-Option-A default).
 *
 * @param {Object} jurisdiction       — must carry `state` and `jurisdiction_type`.
 * @param {string} taxPeriod          — 'current' | 'delinquent'
 * @returns {string|null}
 */
export function resolveTaxCategoryCode(jurisdiction, taxPeriod) {
  if (!jurisdiction) return null;
  const state = String(jurisdiction.state || '').toUpperCase();
  if (state !== 'PA') return null;
  const code = defaultCategoryForPA(jurisdiction.jurisdiction_type, taxPeriod);
  return code || null;
}
